/**
 * CodeBlock — preformatted multi-line monospace block for JSON, log lines,
 * payloads, or troubleshooting output.
 *
 * Use when: showing >1 line of fixed-width content, or any structured payload
 * (request/response, OCPP message, audit detail). For inline tokens use
 * `MonoText`; for IDs use `IDChip`.
 */
import { useState, type ReactNode } from 'react';
import { Button, Tooltip, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { CopyRegular, CheckmarkRegular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  root: {
    position: 'relative',
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalM,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre',
    overflow: 'auto',
    minHeight: 0,
  },
  wrap: {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  copyBtn: {
    position: 'absolute',
    top: tokens.spacingVerticalXS,
    right: tokens.spacingHorizontalXS,
  },
});

interface CodeBlockProps {
  children: ReactNode;
  /** Wrap long lines instead of horizontal scroll. */
  wrap?: boolean;
  /** Show a copy-to-clipboard button. Provide the raw text. */
  copyValue?: string;
  /** Cap height; content scrolls beyond this. */
  maxHeight?: number;
}

export function CodeBlock({ children, wrap, copyValue, maxHeight }: CodeBlockProps) {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!copyValue) return;
    try {
      await navigator.clipboard.writeText(copyValue);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard not available — ignore
    }
  };

  return (
    <div
      className={mergeClasses(styles.root, wrap && styles.wrap)}
      style={maxHeight ? { maxHeight: `${maxHeight}px` } : undefined}
    >
      {copyValue ? (
        <Tooltip content={copied ? 'Copied' : 'Copy'} relationship="label">
          <Button
            className={styles.copyBtn}
            appearance="subtle"
            size="small"
            icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
            onClick={handleCopy}
          />
        </Tooltip>
      ) : null}
      {children}
    </div>
  );
}
